import React from "react";
import { Modal, Button } from "react-bootstrap";
import { getAge } from "../../utils/dateFormat";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import { toastErrorMessage } from "../../utils/utils";

const MembershipPaymentModal = ({
  show,
  onHide,
  members = [],
  primaryRow, // Added prop for primary user awareness if needed
  plan,
  onBack,
}) => {
  const dispatch = useDispatch();

  if (!plan) return null;

  const chargeableMembers = members.filter(
    (m) => getAge(m?.date_of_birth) >= 5
  );

  const discountAmount = plan?.discount_amount || 0;
  const discountTillDate = plan?.discount_till_date || null;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const discountValid =
    !!discountAmount &&
    !!discountTillDate &&
    new Date(discountTillDate).setHours(0, 0, 0, 0) >= todayStart.getTime();

  const effectiveDiscount = discountValid ? discountAmount : 0;
  const totalDiscount = chargeableMembers.length * effectiveDiscount;
  const totalAmount = chargeableMembers.length * plan.amount - totalDiscount;

  const handleProcessToPay = async () => {
    try {
      if (!plan?._id) {
        toastErrorMessage("Plan is not available.");
        return;
      }

      dispatch(SET_LOADING(true));

      // Filter logic: Only include members who are being paid for here.
      // This modal receives `members` which are usually the "pending" or "newly added" ones passed from FamilyMemberModal.
      // We need to send their IDs.
      const familyMemberIds = chargeableMembers.map((m) => m._id);

      // Primary included logic:
      // If we are just adding family members, primary_included is likely false.
      // However, if the user (primary) is also inactive, we might need to handle that?
      // For now, based on FamilyMemberModal usage, this is "Add Family Member" flow.
      // Typically `primary_included` is false here unless we detect otherwise?
      // The `ManageMembership.jsx` logic checked `!user?.is_membership_active`.
      // We'll default strictly to what is passed. If `primaryRow` isn't passed, assume false.
      const primaryIncluded = false;

      const payload = {
        family_members: familyMemberIds,
        payment_type: "membership",
        plan_id: plan._id,
        amount: totalAmount,
        primary_included: primaryIncluded,
      };

      const response = await API.post(
        `${api_paths.member.payment.PROCESS_TO_PAY_MEMBERSHIP}?type=adding-members`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data?.payload?.token;

        // Create a POST form dynamically to open the HPP
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);

        dispatch(SET_LOADING(false));

        form.submit();
      } else {
        throw new Error("No payment token returned");
      }
    } catch (err) {
      console.error("Payment error:", err);
      dispatch(SET_LOADING(false));
      toastErrorMessage(
        err?.response?.data?.message ||
          "Failed to initiate payment. Please try again."
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      {/* Header */}
      <Modal.Header closeButton>
        <Modal.Title className="fw-semibold">Membership Payment</Modal.Title>
      </Modal.Header>

      {/* Body */}
      <Modal.Body className="px-md-4 px-2">
        <div className="px-2 py-2">
          {/* Family Members */}
          <div className="mb-4">
            <label className="fw-semibold mb-2 text-main">Family Members</label>
            <div>
              {members.map((m, i) => {
                const age = getAge(m?.date_of_birth);
                const isFree = age < 5;

                return (
                  <div
                    key={i}
                    className="form-control mb-3 d-flex justify-content-between align-items-center"
                  >
                    <span>
                      {m?.first_name} {m?.last_name}
                    </span>

                    {isFree && (
                      <span className="badge bg-success-subtle text-success px-3 py-3">
                        Free
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Summary */}
          <div>
            <h1 className="fw-semibold mb-3 f-16 text-main">Payment Summary</h1>
            <div className="paymrnt-summary p-3 text-main">
              <div className="d-flex justify-content-between mb-2">
                <span>Plan:</span>
                <span>{plan.plan_name}</span>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <span>Chargeable Members:</span>
                <span>{chargeableMembers.length}</span>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <span>Price per Member:</span>
                <span>${plan.amount}</span>
              </div>

              {discountValid && (
                <div className="d-flex justify-content-between mb-2 text-success">
                  <span>Discount per Member:</span>
                  <span>-${effectiveDiscount}</span>
                </div>
              )}

              {discountValid && (
                <div className="d-flex justify-content-between mb-2 fw-semibold text-success">
                  <span>Total Discount:</span>
                  <span>-${totalDiscount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center p-3 total-payment-summary">
              <span className="fw-semibold">Total Amount:</span>
              <span className="fw-semibold fs-5">
                ${totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        <div className="px-2 py-3">
          <Button className="me-3 btn btn-black-lg" onClick={onBack}>
            Back
          </Button>

          <Button
            className="btn btn-orange-lg"
            onClick={handleProcessToPay}
            disabled={totalAmount <= 0} // Disable if nothing to pay? Or allow free confirmation api? Assuming amount > 0 for payment gateway.
          >
            Pay ${totalAmount.toFixed(2)}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MembershipPaymentModal;
